import { ApiError, apiPost, apiPut } from '@services/apiClient'
import type {
  LoginRequest,
  LoginResponse,
  PreloginResponse,
  RegisterRequest,
} from '@/crypto/key-hierarchy.types'

export interface PreloginRequest {
  email: string
}

/** F5 後の LOCKED 復元用 unlock 素材 (apps/api `RefreshUnlockMaterial` と一致)。 */
export interface RefreshUnlockMaterial {
  email: string
  protectedSymmetricKey: string
  protectedSymmetricKeyIv: string
  kdf: {
    salt: string
    memoryCost: number
    timeCost: number
    parallelism: number
  }
}

export interface RefreshResponse {
  accessToken: string
  /**
   * B5: F5 / タブ再読込で in-memory session が消えた後に LOCKED 状態を復元するための
   * 封印済素材。古いサーバは返さないため optional (未対応時は従来どおり再ログイン)。
   */
  material?: RefreshUnlockMaterial
}

export interface RegisterResponse {
  id: string
  email: string
}

export const prelogin = (email: string): Promise<PreloginResponse> =>
  apiPost<PreloginResponse, PreloginRequest>('/auth/prelogin', { email })

export const login = (request: LoginRequest): Promise<LoginResponse> =>
  apiPost<LoginResponse, LoginRequest>('/auth/login', request)

export const register = (request: RegisterRequest): Promise<RegisterResponse> =>
  apiPost<RegisterResponse, RegisterRequest>('/auth/register', request)

/**
 * POST /api/auth/resend-verification
 * 未認証ユーザーへ register 検証メールを再送する。Phantom 対策でサーバは常に
 * `{ ok: true }` を返す (未登録 / 認証済みでも応答は同一)。
 */
export const resendVerification = (email: string): Promise<{ ok: true }> =>
  apiPost<{ ok: true }, { email: string }>('/auth/resend-verification', { email })

// ── Recovery Key 経路 (T-C5b、KEY_HIERARCHY.md §4.7) ──────────────

/**
 * POST /api/auth/recovery-prepare リクエスト body。
 * apps/api/src/auth/dto/recovery.dto.ts `RecoveryPrepareDto` と一致。
 */
export interface RecoveryPrepareRequest {
  email: string
}

/**
 * POST /api/auth/recovery-prepare レスポンス。
 * Phantom user 対策: 未登録 / recovery 列 NULL でも deterministic な fake salt を返すため、
 * レスポンスからユーザー存在は判別不能 (常に 200)。
 */
export interface RecoveryPrepareResponse {
  /** HKDF salt (hex 32 文字 = 16 byte) */
  recoveryKdfSalt: string
  /** Recovery KDF 仕様のバージョン (1 = HKDF-SHA256) */
  recoveryKdfVersion: number
}

export const recoveryPrepare = (email: string): Promise<RecoveryPrepareResponse> =>
  apiPost<RecoveryPrepareResponse, RecoveryPrepareRequest>('/auth/recovery-prepare', { email })

/**
 * POST /api/auth/recovery-verify リクエスト body。
 * apps/api/src/auth/dto/recovery.dto.ts `RecoveryVerifyDto` と一致。
 */
export interface RecoveryVerifyRequest {
  email: string
  /** HKDF-SHA256(seed, salt=recoveryKdfSalt, info="OoKey/recovery/verify/v1") の hex (32 byte) */
  recoveryProof: string
}

/**
 * POST /api/auth/recovery-verify レスポンス (200)。
 * 失敗時 (proof 不一致 / 未登録 / NULL recovery 列) は ApiError(401)。
 */
export interface RecoveryVerifyResponse {
  /** type='recovery' の short-lived JWT (5 分 TTL、single-use)。reset で消費 */
  recoverySessionToken: string
  /** Recovery KEK で AES-256-GCM 暗号化された SK (base64) */
  recoveryProtectedSymmetricKey: string
  /** 上記 AES-GCM IV (base64, 12 byte) */
  recoveryProtectedSymmetricKeyIv: string
  /** ユーザー email (Recovery Key 経路後の re-login で再入力させずに済む) */
  email: string
}

export const recoveryVerify = (request: RecoveryVerifyRequest): Promise<RecoveryVerifyResponse> =>
  apiPost<RecoveryVerifyResponse, RecoveryVerifyRequest>('/auth/recovery-verify', request)

/**
 * PUT /api/auth/recovery/reset リクエスト body (T-C5e)。
 * apps/api/src/auth/dto/recovery.dto.ts `RecoveryResetDto` と一致。
 *
 * email は body に含めない (サーバが Bearer recoverySessionToken の `sub` から userId を解決)。
 * 全フィールドはクライアント側で派生済の値 (新 MP は決して送信しない)。
 */
export interface RecoveryResetRequest {
  /** 新 SRP-6a salt (hex) */
  srpSalt: string
  /** 新 SRP-6a verifier v = g^x mod N (hex) */
  srpVerifier: string
  /** Argon2id KDF パラメータ (新 MK 派生時のもの) */
  kdfMemoryKib: number
  kdfTimeCost: number
  kdfParallelism: number
  /** 新 Argon2id salt (hex) */
  kdfSalt: string
  /** 新 SMK_enc で SK を再封印した結果 (base64, ciphertext || authTag) */
  protectedSymmetricKey: string
  /** 上記 AES-GCM IV (base64, 12 byte) */
  protectedSymmetricKeyIv: string
}

/**
 * PUT /api/auth/recovery/reset
 * Bearer recoverySessionToken で認可。成功時 204 (body なし)。
 *
 * 失敗系は ApiError(401) を throw する:
 *   - token 不正 / 期限切れ (5 分超) / typ ≠ recovery
 *   - jti 失効済 (single-use 強制 — 2 回目の reset は弾く)
 *   - user 削除済
 */
export const recoveryReset = (
  request: RecoveryResetRequest,
  recoverySessionToken: string,
): Promise<void> =>
  apiPut<void, RecoveryResetRequest>('/auth/recovery/reset', request, {
    bearerToken: recoverySessionToken,
  })

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/**
 * POST /api/auth/refresh
 * Refresh cookie (HttpOnly) を送って新しい access token を受け取る。
 * 失敗時は ApiError(401) を throw する。
 */
export const refresh = async (): Promise<RefreshResponse> => {
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    throw new ApiError(res.status, `POST /auth/refresh failed: ${res.status}`)
  }

  return (await res.json()) as RefreshResponse
}

/**
 * POST /api/auth/logout
 * Refresh cookie を削除する (204)。失敗しても呼び出し元はセッション破棄を続行できる。
 */
export const logout = async (): Promise<void> => {
  const res = await fetch(`${BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  })

  if (!res.ok) {
    throw new ApiError(res.status, `POST /auth/logout failed: ${res.status}`)
  }
}
